/**
 * Flower drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_doodles`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const flowerDrawingsPack: ToilePack = {
    slug: 'flower-drawings',
    category: 'coloring',
    name: 'Flower drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'rose', label: 'Rose', subject: 'a single open rose on a long stem with two leaves and a few thorns, the petals spiralling from the center'},
        {key: 'bouquet', label: 'Bouquet', subject: "a bouquet of peonies, tulips and baby's breath wrapped in paper and tied with a bow"},
        {key: 'lotus', label: 'Lotus', subject: 'a lotus flower open on the water, a round lily pad beside it and a closed bud on a tall stem'},
    ],
    styles: [
        {key: 'botanical', label: 'Botanical ink', style: 'a botanical ink illustration, fine black lines with stippled shading on the petals and leaves', palette: 'Black ink only.', framing: 'One drawing centered on the page with a wide white margin.'},
        {key: 'easy', label: 'Easy outline', style: 'a bold simple outline with a few clean inner lines, open shapes easy to copy and color', palette: 'Black ink only, no shading, no gray tones.', framing: 'One drawing centered on the page with a wide white margin.'},
        {key: 'pencil', label: 'Colored pencil', style: 'a black outline softened with layered colored pencil in petal shades', palette: 'Petal and leaf colors only, the ground pure white.', framing: 'One drawing centered on the page with a wide white margin.'},
    ],
    directions: ['sunflower', 'tulips', 'wildflowers'],
    directionHint: 'try: sunflower',
    seo: {
        title: 'Flower Drawings: Single Rose, Bouquet, Lotus',
        metaDescription: 'Nine flower drawings, a rose, a bouquet and a lotus, in botanical ink, easy outline and colored pencil for art class or framing.',
        h1: 'Flower drawings: rose, bouquet, lotus',
        tagline: 'One flower subject per page, drawn with three levels of care.',
        intro: 'Flower drawings are single flowers and bouquets drawn carefully enough to frame, color or copy for an art class. These nine pages show one flower subject each, centered on white: a rose, a bouquet and a lotus. Each comes as a botanical ink illustration, a bold simple outline, or a black outline with colored pencil. For a different flower, type it in the direction box.',
        useCases: [
            'A copy page in an art class',
            'A print to frame',
            'A card illustration',
            'A coloring page for an adult',
        ],
        faq: [
            {q: 'What is the botanical ink style?', a: 'Fine black lines with stippled shading on the petals and leaves, drawn in black ink only.'},
            {q: 'Is the colored pencil style fully colored?', a: 'No. It is a black outline with layered colored pencil in petal and leaf shades, and the ground is left white.'},
            {q: 'Can I ask for a different flower?', a: 'Yes. Type it in the direction box, such as a sunflower, tulips or wildflowers. A typed flower is a request, so the result may differ from the samples.'},
        ],
    },
}
